/**
 * Human-readable IDs, folder and file naming (spec §4, §6.3, §7.3).
 */

export type SequenceKind = 'C' | 'J';

const HUMAN_ID_RE = /^DC-([CJ])-(\d{4})-(\d{4,})$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function formatHumanId(kind: SequenceKind, year: number, n: number): string {
  return `DC-${kind}-${year}-${String(n).padStart(4, '0')}`;
}

export interface ParsedHumanId {
  kind: SequenceKind;
  year: number;
  number: number;
  id: string;
}

export function parseHumanId(value: string): ParsedHumanId | null {
  const m = HUMAN_ID_RE.exec(value.trim());
  if (!m) return null;
  const kind = m[1].toUpperCase() as SequenceKind;
  const year = Number(m[2]);
  const number = Number(m[3]);
  return { kind, year, number, id: formatHumanId(kind, year, number) };
}

export function isCandidateId(value: string): boolean {
  return parseHumanId(value)?.kind === 'C';
}

export function isJdReference(value: string): boolean {
  return parseHumanId(value)?.kind === 'J';
}

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** "v01", "v02", ... */
export function versionTag(version: number): string {
  return `v${String(version).padStart(2, '0')}`;
}

export function stripDiacritics(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss').replace(/æ/gi, 'ae').replace(/ø/gi, 'o');
}

const ROLE_STOPWORDS = new Set([
  'and',
  'or',
  'of',
  'the',
  'for',
  'a',
  'an',
  'in',
  'with',
  'to',
  'und',
  'oder',
  'fuer',
  'fur',
  'der',
  'die',
  'das',
  'm',
  'w',
  'd',
  'f',
  'x',
]);

/**
 * RoleShort: 2–4 words, CamelCase, ASCII (spec §7.3).
 * "Senior AI Engineer / Data Engineer" → "SeniorAIDataEngineer"; "Tech Lead AI / Full-Stack" → "TechLeadAI".
 * Segments (split on / , | · ; and dashes between spaces) are merged while the result stays within 4 words;
 * duplicate words keep their last occurrence. Parentheticals are dropped.
 */
export function roleShort(role: string, maxWords = 4): string {
  const cleaned = stripDiacritics(role)
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ');
  const segments = cleaned
    .split(/\s*[/,|·;]\s*|\s+[–—-]\s+/)
    .map((seg) => segmentWords(seg))
    .filter((w) => w.length > 0);
  if (segments.length === 0) return 'Role';

  let words: string[] = [];
  for (const seg of segments) {
    const merged = dedupeKeepLast([...words, ...seg]);
    if (words.length > 0 && merged.length > maxWords) break;
    words = merged;
    if (words.length >= maxWords) break;
  }
  if (words.length > maxWords) words = words.slice(0, maxWords);
  return words.map(camelWord).join('') || 'Role';
}

function segmentWords(segment: string): string[] {
  return segment
    .split(/[^A-Za-z0-9+#]+/)
    .map((w) => w.replace(/[+#]/g, (c) => (c === '+' ? 'Plus' : 'Sharp')))
    .filter((w) => w.length > 0 && !ROLE_STOPWORDS.has(w.toLowerCase()));
}

function dedupeKeepLast(words: string[]): string[] {
  const result: string[] = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i].toLowerCase();
    if (words.slice(i + 1).some((x) => x.toLowerCase() === w)) continue;
    result.push(words[i]);
  }
  return result;
}

function camelWord(word: string): string {
  if (word.length <= 3 && word === word.toUpperCase()) return word; // AI, SAP, QA
  if (/^[A-Z0-9]+$/.test(word)) return word; // ABAP, HANA
  if (/^[A-Z][a-z]+[A-Z]/.test(word)) return word; // DevOps, SuccessFactors
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Safe for Drive/Windows/macOS file names: replaces "/" with "-", drops control characters. */
export function sanitizeFileName(name: string): string {
  return name
    .replace(/\//g, '-')
    .replace(/[\\:*?"<>|]/g, '-')
    .split('')
    .filter((ch) => {
      const c = ch.charCodeAt(0);
      return c >= 32 && c !== 127;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "Andrei Popescu" → "Andrei_Popescu" (ASCII). */
export function nameToFolderPart(fullName: string): string {
  return stripDiacritics(fullName)
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('_');
}

/** "AmberDigital" from "Amber Digital" / "Mircea / Amber Digital" (last segment when a slash is present). */
export function shortCodeFromName(name: string): string {
  const seg = name.includes('/') ? name.split('/').pop()! : name;
  return stripDiacritics(seg)
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

export interface CandidateFolderInput {
  candidateId: string;
  fullName?: string | null;
  supplierShortCode?: string | null;
  role?: string | null;
}

/** `DC-C-2026-0009_First_Last` or `DC-C-2026-0009_AmberDigital_TechLeadAI` (spec §6.3, §7.3). */
export function candidateFolderName(input: CandidateFolderInput): string {
  if (input.fullName && input.fullName.trim()) {
    return `${input.candidateId}_${nameToFolderPart(input.fullName)}`;
  }
  const parts = [input.candidateId];
  if (input.supplierShortCode) parts.push(sanitizeAscii(input.supplierShortCode));
  if (input.role) parts.push(roleShort(input.role));
  return parts.join('_');
}

function sanitizeAscii(s: string): string {
  return stripDiacritics(s).replace(/[^A-Za-z0-9]+/g, '');
}

/** `<request_no>_<RoleShort>` */
export function positionFolderName(
  requestNo: string,
  role: string,
  roleShortOverride?: string | null,
): string {
  return `${sanitizeAscii(requestNo) || 'REQ'}_${roleShortOverride ? sanitizeAscii(roleShortOverride) : roleShort(role)}`;
}

/** `DC-J-YYYY-NNNN_<request_no>_JobDescription_<EN|DE>_vNN` */
export function jdFileBase(reference: string, requestNo: string, lang: 'EN' | 'DE', version: number): string {
  return `${reference}_${sanitizeAscii(requestNo)}_JobDescription_${lang}_${versionTag(version)}`;
}

/** `DC-C-YYYY-NNNN_<request_no>_CandidateProfile_<EN|DE>_vNN` (request_no omitted for generic profiles). */
export function profileFileBase(
  candidateId: string,
  requestNo: string | null | undefined,
  lang: 'EN' | 'DE',
  version: number,
): string {
  const req = requestNo ? `_${sanitizeAscii(requestNo)}` : '';
  return `${candidateId}${req}_CandidateProfile_${lang}_${versionTag(version)}`;
}

/** `DC-J-YYYY-NNNN - <JD title with "/" replaced by "-">.pdf` for the open-JDs folder. */
export function openJdCopyName(reference: string, title: string): string {
  return `${reference} - ${sanitizeFileName(title)}.pdf`;
}

/** "First S." when the name is known, else "Candidate DC-C-YYYY-NNNN" (spec §4.3, §6.4). */
export function candidateDisplayName(fullName: string | null | undefined, candidateId: string): string {
  const name = (fullName ?? '').trim().replace(/\s+/g, ' ');
  if (!name) return `Candidate ${candidateId}`;
  const parts = name.split(' ');
  const first = parts[0];
  if (parts.length === 1) return first;
  const last = parts[parts.length - 1];
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

/** Text of a submission: "DC-C-… → <request_no> <role>" */
export function submissionTitle(candidateId: string, requestNo: string, role: string): string {
  return `${candidateId} → ${requestNo} ${role}`;
}
